import type { User } from '@datadog/browser-core';
import { datadogRum } from '@datadog/browser-rum';

import { getRegion } from '@adsk/acs-sc-web-platform-api';
import { createMonitoringListener, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import newRelicErrorHandler from './newRelicErrorHandler';

let monitoringInitialized = false;
let monitoringListener:
  | {
      dispose(): void;
    }
  | undefined;

declare global {
  interface Window {
    newrelic: {
      noticeError?: (error: Error, metadata: MetadataType) => void;
      setErrorHandler?: (callback: () => void) => void;
    };
  }
}

interface MetadataType {
  [key: string]: unknown;
}

function reportError(error: Error | string, metadata: MetadataType) {
  if (!monitoringInitialized) {
    throw new Error(
      'Monitoring is not initialized. Please call initializeMonitoring() before calling reportError()',
    );
  }

  error = typeof error === 'string' ? new Error(error) : error;

  const isLegacyNewRelicExists = Boolean(window.newrelic?.noticeError);

  /**
   *  No need to report datadog error if newrelic exists.
   *  We override the original "noticeError" of newrelic function and report the error to datadog from there.
   *
   *  See "initializeErrorHandler" function for more details.
   */
  if (isLegacyNewRelicExists) {
    window?.newrelic?.noticeError?.(error, metadata);
  } else {
    datadogRum.addError(error, { stack: error.stack, ...(metadata ?? {}) });
  }
}

function reportWarning(error: Error | string, metadata: MetadataType) {
  reportError(error, { diagnosticType: 'warning', ...metadata });

  console.warn(error);
}

export function setUserToErrorContext(
  user: User & { analyticsId?: string | null },
) {
  datadogRum.setUser(user);
  datadogRum.setGlobalContextProperty('hasUser', true);

  if (user.analyticsId) {
    datadogRum.setGlobalContextProperty('analyticsId', user.analyticsId);
  }
}

export function removeUserFromErrorContext() {
  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('hasUser');
  datadogRum.removeGlobalContextProperty('analyticsId');
}

export function disposeMonitoringContext() {
  if (!monitoringInitialized) {
    return;
  }

  removeUserFromErrorContext();
}

export function setCurrentToolScopeToContext(scope: string) {
  datadogRum.setGlobalContextProperty('exo-current-tool-scope', scope);
}

export function removeCurrentToolScopeFromContext() {
  datadogRum.removeGlobalContextProperty('exo-current-tool-scope');
}

export function disposeMonitoring() {
  if (monitoringInitialized) {
    monitoringInitialized = false;

    if (monitoringListener) {
      monitoringListener.dispose();
    }
  }
}

function initializeErrorHandler() {
  if (
    window.newrelic &&
    typeof window.newrelic.setErrorHandler === 'function'
  ) {
    window.newrelic.setErrorHandler(newRelicErrorHandler);

    const originalNoticeError = window.newrelic.noticeError;

    window.newrelic.noticeError = function (
      error: Error,
      options: MetadataType,
    ) {
      datadogRum.addError(error, options);

      return originalNoticeError?.call(this, error, options);
    };
  }
}

export function initializeMonitoringHandler() {
  if (!monitoringInitialized) {
    monitoringInitialized = true;

    monitoringListener = createMonitoringListener({
      onReport({ data: { type, value, context } }) {
        switch (type) {
          case REPORT_TYPES.error:
            reportError(value, context);
            break;
          case REPORT_TYPES.warning:
            reportWarning(value, context);
            break;
          case REPORT_TYPES.info:
            datadogRum.addAction(typeof value === 'string' ? value : 'info', {
              value,
              ...(context || {}),
            });
            break;
          default:
            break;
        }
      },
    });

    initializeErrorHandler();
  }
}

export function initializeMonitoringMetadata() {
  datadogRum.setGlobalContextProperty('region', getRegion());

  const version = process.env.APPLICATION_VERSION;
  const buildIdentifier = process.env.APPLICATION_UNIQUE_BUILD_ID;

  if (!version) {
    throw new Error(
      'APPLICATION_VERSION is not defined. Please check your webpack config.',
    );
  }

  if (!buildIdentifier) {
    throw new Error(
      'APPLICATION_UNIQUE_BUILD_ID is not defined. Please check your webpack config.',
    );
  }

  datadogRum.setGlobalContextProperty('exo-version', version);
  datadogRum.setGlobalContextProperty('exo-build-identifier', buildIdentifier);
}

export const REPORT_EVENT_TYPES = {
  REMOTE_MODULE_ERROR: 'remote-module-error',
  ROOT_ERROR: 'root-error-boundary',
  PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
  DEPRECATED_ROUTE_OR_REDIRECT: 'DEPRECATED_ROUTE_OR_REDIRECT',
};
